/* Copyright (c) 2020-present CloudIO Inc */

import { FieldType } from '@cloudio-saas/datasource-types';
import { ListItemButton } from '@mui/material';
import { Grid, InputBase, Theme, List, ListItemText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import VirtualList from 'components/DataGrid/grid/List';
import Icon from 'icons/Icon';
import { PlatformContext } from 'platform/PlatformProvider';
import React, {
  CSSProperties,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';
import { useAttributes } from 'store/DSAttributesCache';

export type AttributeInfo = {
  attributeCode: string;
  attributeName?: string;
  attributeType: FieldType;
  tip?: string;
};

type Props = {
  dsAlias: string;
  onSelect: (text: string) => void;
  height: number;
  width: number;
  hideSearch?: boolean;
  pageUid: string;
  appUid: string;
};

const JSON: FieldType = 'JSON';
const Boolean: FieldType = 'Boolean';
const String: FieldType = 'String';
const Integer: FieldType = 'Integer';

const dsAttrs: AttributeInfo[] = [
  {
    attributeCode: '$rows',
    tip: 'returns an array of all available rows in the store',
    attributeType: JSON,
  },
  {
    attributeCode: '$selectedRows',
    tip: 'returns an array of selected rows in the store',
    attributeType: JSON,
  },
  {
    attributeCode: '$dirtyRows',
    tip: 'returns an array of inserted and updated rows in the store that are eligible to be posted to the server on save.',
    attributeType: JSON,
  },
  {
    attributeCode: '$busy',
    tip: 'returns true if the query or post is being performed on this data store',
    attributeType: Boolean,
  },
  {
    attributeCode: '$isCurrentRecord',
    tip: 'returns true if the record index is same as the current record index i.e. active record',
    attributeType: Boolean,
  },
  {
    attributeCode: '$currentRecordIndex',
    tip: 'returns the current record index. -1 when no records exists in the store',
    attributeType: String,
  },
  {
    attributeCode: '$isFromDB',
    tip: 'returns true if the record is fetched from the server',
    attributeType: Boolean,
  },
  {
    attributeCode: '$dirty',
    tip: 'returns true if there are any unsaved changes in this store',
    attributeType: Boolean,
  },
  {
    attributeCode: '$hasInitialQueryFired',
    tip: 'returns true if the store has been already queried at least once',
    attributeType: Boolean,
  },
  {
    attributeCode: '$hasFilters',
    tip: 'returns true if any filters have been applied to this store. the filters could be either from Grid Header, Quick Filter or Smart Search',
    attributeType: Boolean,
  },
  {
    attributeCode: '$selectedRowCount',
    tip: "returns the total number of records selected using the Grid's RowSelection",
    attributeType: Integer,
  },
  {
    attributeCode: '$size',
    tip: 'returns the size of the store. i.e. total number of records fetched from the server so far plus any additions, minus any deletions done by the user',
    attributeType: Integer,
  },
  {
    attributeCode: '$totalRowCount',
    tip: 'returns the total row count of the store. i.e. total number of records available in the server',
    attributeType: Integer,
  },
].sort((a, b) => a.attributeCode.localeCompare(b.attributeCode));

const dsAttrsMap = dsAttrs.reduce((acc, a) => {
  acc[a.attributeCode] = a;
  return acc;
}, {} as Record<string, AttributeInfo>);

const isFixed = (code: string) => !!dsAttrsMap[code];

const isSubAttr = (code: string) => code?.startsWith('$orig.');

const useStyles = makeStyles((theme: Theme) => ({
  listItem: {},
  active: {
    backgroundColor: theme.palette.action.selected,
  },
  title: {
    backgroundColor: theme.palette.background.default,
    lineHeight: '24px',
    padding: 8,
  },
  fixedAttrs: {
    borderLeft: '4px solid ' + theme.palette.warning.main,
  },
  subAttrs: {
    borderLeft: '4px solid ' + theme.palette.info.main,
  },
}));

const DataSourceProperties = ({
  dsAlias,
  onSelect,
  height,
  width,
  hideSearch,
  appUid,
  pageUid,
}: Props) => {
  const classes = useStyles();
  const platform = useContext(PlatformContext);
  const store = dsAlias ? platform.getStore(appUid, pageUid, dsAlias) : null;
  const attributes = useAttributes(appUid, store?.ds ?? '$MISSING_DS$');
  const [selectedProp, setSelectedProp] = useState('');
  const [filter, setFilter] = useState('');

  useLayoutEffect(() => {
    if (store) {
      store.initializeAttributes();
    }
  }, [store]);

  if (!store) {
    return null;
  }

  const attributesFromStore: AttributeInfo[] = attributes.map((a) => ({
    attributeCode: a.attributeCode,
    attributeName: a.attributeName,
    attributeType: a.attributeType,
  }));

  const origAttributesFromStore: AttributeInfo[] = attributes.map((a) => ({
    attributeCode: '$orig.' + a.attributeCode,
    attributeName: 'Original ' + a.attributeName,
    attributeType: a.attributeType,
    tip: `returns the original/initial value of the attribute before any changes were made.

This is useful when you want to compare the current value with the original value.

e.g. 
\`\`\`javascript
$.employeesAlias.$orig.endDate ? isBefore($.employeesAlias.endDate, $.employeesAlias.$orig.endDate) : false
\`\`\`

\`\`\`alert
Note: If no changes are made to this attribute, then it returns undefined. If changes are made and if there was no original value, then it returns null.
\`\`\`
`,
  }));

  let allAttributes = [
    ...attributesFromStore,
    ...dsAttrs,
    ...origAttributesFromStore,
  ];

  if (filter) {
    const lcfilter = filter.toLowerCase();
    allAttributes = allAttributes.filter(
      (r) =>
        r &&
        ((r.attributeName &&
          r.attributeName.toLowerCase().indexOf(lcfilter) !== -1) ||
          (r.attributeCode &&
            r.attributeCode.toLowerCase().indexOf(lcfilter) !== -1) ||
          (r.attributeType &&
            r.attributeType.toLowerCase().indexOf(lcfilter) !== -1)),
    );
  }

  const hideSearchInput = hideSearch || (!filter && allAttributes.length < 6);

  const searchInput = (
    <Grid container item xs>
      <Grid item xs="auto">
        <Icon iconName="search" style={{ display: 'flex', margin: '8px' }} />
      </Grid>
      <Grid item xs>
        <InputBase
          fullWidth
          placeholder="Search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </Grid>
    </Grid>
  );

  const onClick = (value?: string) => {
    setSelectedProp(value ?? '');
    onSelect(`$.${dsAlias}.${value}`);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div>
        <div className={classes.title}>Choose an Attribute</div>
        {hideSearchInput ? null : searchInput}
      </div>
      <List>
        <VirtualList
          CellComponent={({ rowIndex, style }) => (
            <AppCell
              style={style}
              label={
                allAttributes[rowIndex].attributeName ||
                allAttributes[rowIndex].attributeCode
              }
              attributeCode={allAttributes[rowIndex].attributeCode}
              value={allAttributes[rowIndex].attributeCode}
              onClick={onClick}
              data-tip={allAttributes[rowIndex].tip}
              active={selectedProp === allAttributes[rowIndex].attributeCode}
            />
          )}
          rowHeight={32}
          height={height - 40 - (hideSearchInput ? 0 : 32) - 16}
          totalRows={allAttributes.length}
          width={width}
          custom
        />
      </List>
    </div>
  );
};

export default DataSourceProperties;

function AppCell({
  value,
  style,
  onClick,
  label,
  attributeCode,
  active,
  'data-tip': dataTip,
}: {
  value: string;
  label?: string;
  attributeCode: string;
  style?: CSSProperties;
  onClick: (prop?: string) => void;
  // store: Store<IoDatasourceAttributes>;
  active: boolean;
  'data-tip'?: string;
}) {
  const classes = useStyles();
  return (
    <ListItemButton
      className={clsx(
        classes.listItem,
        active ? classes.active : null,
        isFixed(attributeCode) ? classes.fixedAttrs : null,
        isSubAttr(attributeCode) ? classes.subAttrs : null,
      )}
      key={value}
      style={style}
      dense
      onClick={() => {
        onClick(attributeCode);
      }}
      data-tip={
        isFixed(attributeCode) ? dsAttrsMap[attributeCode].tip : dataTip
      }
      data-tip-delay={0}
      data-tip-at="left"
      data-tip-markdown
      data-tip-width={500}
    >
      <ListItemText primary={label || 'None'} />
    </ListItemButton>
  );
}
